
import Vue, { VueConstructor } from 'vue';

// Components
import Spinner from './Spinner.vue';

// Utils
import { mixin as clickaway } from 'vue-clickaway';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';

interface ActionOptions {
    id?: string;
    title: string;
    variant: string;
    action: (resolver: { res: (value: unknown) => void; rej: (reason?: any) => void }) => unknown;
}

interface State {
    uuid: string;
    open?: boolean;
    resolver?: { res: (value: unknown) => void; rej: (reason?: any) => void } | null;
    redirect?: string | null;
    title?: string | null;
    content?: string | null;
    contentHTML?: string | null;
    actions: ActionOptions[];
    center?: boolean;
    onClose: (resolver: { res: (value: unknown) => void; rej: (reason?: any) => void }) => void;
    component?: VueConstructor<Vue> | null;
    isError?: boolean;
    windowMode?: boolean; // Allows opening of modal components without any extra functionality other than what comes with base modal, usefull for creating completely custom UX
    payload?: any; // Allows for passing data through to a expecting dialog without using any of the fields that affect the dialog view
    props?: object; // Allows for passing data through a given component as props,
    listeners?: object; // Allows for passing v-on-listeners to a given component,
    order?: number;
    clickawayOff?: boolean;
}

interface DialogUpdateContext {
    disabledActions: {
        [key: string]: boolean;
    };
}

const newUpdateContext = (): DialogUpdateContext => ({
    disabledActions: {},
});

export default Vue.extend({
    name: 'Dialog',
    mixins: [clickaway],
    data() {
        return {
            targetElement: null as HTMLElement | null,
            isLoadingAction: null as string | null,

            updateContext: newUpdateContext(),
        };
    },
    computed: {
        state(): State {
            return this.$interfaces.dialogModal.getState();
        },
        show(): boolean {
            return !!this.state.open;
        },
        order(): State['order'] {
            return this.state.order;
        },
    },
    methods: {
        title(): State['title'] {
            return this.state.title;
        },
        content(): State['content'] {
            return this.state.content;
        },
        contentHTML(): State['contentHTML'] {
            return this.state.contentHTML;
        },
        actions(): State['actions'] {
            return this.state.actions;
        },
        props(): object {
            return this.state.props || {};
        },
        listeners(): object {
            return this.state.listeners || {};
        },
        clickawayOff(): boolean {
            return this.state.clickawayOff === true;
        },
        callAction(action: ActionOptions['action'], actionName: string) {
            const result: any = action(this.state.resolver!);
            const isPromise = result && typeof result.then === 'function';
            if (isPromise) {
                this.isLoadingAction = actionName;
                result.finally(() => {
                    this.isLoadingAction = null;
                });
            }
        },
        close() {
            this.state.onClose(this.state.resolver!);
        },
        onKey(event: KeyboardEvent) {
            if (event.key === 'Escape') {
                this.close();
            }
        },
        optionalComponent(): State['component'] {
            return this.state.component;
        },
        isError(): State['isError'] {
            return this.state.isError;
        },
        isButtonDisabledByContext(action: ActionOptions): boolean {
            return !!this.updateContext.disabledActions[action.id || action.title];
        },
        handleUpdateDialogContext(ctx: Partial<DialogUpdateContext>) {
            this.updateContext = {
                ...this.updateContext,
                ...ctx,
                disabledActions: {
                    ...this.updateContext?.disabledActions,
                    ...ctx.disabledActions,
                },
            };
        },
    },
    watch: {
        show(shouldShow) {
            if (shouldShow) {
                document.addEventListener('keyup', this.onKey);
                disableBodyScroll(this.targetElement!);

                this.updateContext = newUpdateContext();
            } else {
                document.removeEventListener('keyup', this.onKey);
                enableBodyScroll(this.targetElement!);
            }
        },
    },
    mounted() {
        this.targetElement = document.getElementById('dialog');
    },
    components: { Spinner },
});
